import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl, apiFetch } from '../../config/api';
import { useMessages } from '../../components/Messages';
import ResizablePanels from '../../components/Layout/ResizablePanels';
import WarehouseLocationsList from './WarehouseLocationsList';
import ViewWarehouseLocation from './ViewWarehouseLocation';
import CycleCountsManager from './CycleCountsManager';
import FormulationsManager from '../Inventory/FormulationsManager';
import ExtrusionsManager from '../Inventory/ExtrusionsManager';
import LocationSelect from '../../components/Form/LocationSelect';
import ViewInventoryItem from '../Inventory/ViewInventoryItem';
import './WarehouseDashboard.css';
import { formatAppDate } from '../../utils/dateUtils';
import { showApiError } from '../../utils/errorUtils';

const InventoryManagement = forwardRef(({ activeSubTab, openItem, onItemClosed, initialStockFilter }, ref) => {
  const { t } = useTranslation('warehouse');
  const { t: tCommon } = useTranslation('common');
  const { user } = useAuth();
  const { showSuccess, showError, showWarning } = useMessages();

  // Materials inventory states
  const [materials, setMaterials] = useState([]);
  const [inventoryStats, setInventoryStats] = useState({ total_materials: 0, low_stock: 0, out_of_stock: 0, overstocked: 0 });
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [stockFilter, setStockFilter] = useState(initialStockFilter || '');
  // Item detail shown in place of the materials list (opened from the list, /inventory/items/:id or a dashboard)
  const [viewItemId, setViewItemId] = useState(null);
  const [itemEditMode, setItemEditMode] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [adjustmentType, setAdjustmentType] = useState(t('adjustmentTypes.increase'));
  const [adjustmentQuantity, setAdjustmentQuantity] = useState('');
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [isSubmittingAdjustment, setIsSubmittingAdjustment] = useState(false);
  const [adjustmentLocation, setAdjustmentLocation] = useState('');
  const [adjustmentStockLocations, setAdjustmentStockLocations] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  // Put-away section states
  const [putAwayItems, setPutAwayItems] = useState([]);
  const [loadingPutAwayItems, setLoadingPutAwayItems] = useState(false);
  const [warehouseLocations, setWarehouseLocations] = useState([]);
  const [showAssignLocationModal, setShowAssignLocationModal] = useState(false);
  const [selectedPutAwayItem, setSelectedPutAwayItem] = useState(null);
  const [targetLocation, setTargetLocation] = useState('');
  const [assignmentNotes, setAssignmentNotes] = useState('');
  const [submittingAssignment, setSubmittingAssignment] = useState(false);

  // Bulk assignment states
  const [selectedItems, setSelectedItems] = useState([]);
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [bulkTargetLocation, setBulkTargetLocation] = useState('');
  const [bulkAssignmentNotes, setBulkAssignmentNotes] = useState('');
  const [submittingBulkAssignment, setSubmittingBulkAssignment] = useState(false);

  // General inventory transfer states
  const [transferTab, setTransferTab] = useState('putaway'); // 'putaway' or 'general'
  const [locationInventory, setLocationInventory] = useState([]);
  const [loadingLocationInventory, setLoadingLocationInventory] = useState(false);
  const [selectedLocationFilter, setSelectedLocationFilter] = useState('');
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedInventoryItem, setSelectedInventoryItem] = useState(null);
  const [transferFromLocation, setTransferFromLocation] = useState('');
  const [transferToLocation, setTransferToLocation] = useState('');
  const [transferQuantity, setTransferQuantity] = useState('');
  const [transferReason, setTransferReason] = useState('');
  const [submittingTransfer, setSubmittingTransfer] = useState(false);

  // Warehouse locations management states
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [locationsViewMode, setLocationsViewMode] = useState('list');
  const locationsListRef = useRef();
  const locationsViewRef = useRef();

  // Expose refresh method to parent component
  useImperativeHandle(ref, () => ({
    refresh: () => {
      if (activeSubTab === 'inventory') {
        fetchMaterials();
        fetchInventoryStats();
      } else if (activeSubTab === 'storage') {
        fetchPutAwayItems();
        fetchWarehouseLocations();
        if (transferTab === 'general') {
          fetchLocationInventory(selectedLocationFilter);
        }
      } else if (activeSubTab === 'locations') {
        if (locationsListRef.current) {
          locationsListRef.current.fetchLocations();
        }
      }
    }
  }));

  // Open the item requested by the parent (URL /inventory/items/:id); the nonce reopens it if already mounted.
  // When the parent drops it (user left the item URL), close it again.
  const openedByParent = useRef(false);
  useEffect(() => {
    if (openItem?.id) {
      openedByParent.current = true;
      setViewItemId(openItem.id);
      setItemEditMode(false);
    } else if (openedByParent.current) {
      openedByParent.current = false;
      setViewItemId(null);
      setItemEditMode(false);
    }
  }, [openItem?.nonce]);

  // Dashboard cards can open the list pre-filtered (e.g. low stock)
  useEffect(() => {
    if (initialStockFilter) setStockFilter(initialStockFilter);
  }, [initialStockFilter]);

  const closeItemView = () => {
    openedByParent.current = false;
    setViewItemId(null);
    setItemEditMode(false);
    if (onItemClosed) onItemClosed();
  };

  // Debounce material search — wait 300ms after typing stops
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch materials when debounced search or type filter changes
  useEffect(() => {
    if (activeSubTab === 'inventory') {
      fetchMaterials();
    }
  }, [debouncedSearch, typeFilter]);

  // Fetch stats and warehouse locations once on mount
  useEffect(() => {
    fetchInventoryStats();
    fetchWarehouseLocations();
  }, []);

  // Fetch data when subtab changes
  useEffect(() => {
    if (activeSubTab === 'inventory') {
      fetchMaterials();
    } else if (activeSubTab === 'storage') {
      fetchPutAwayItems();
      fetchWarehouseLocations();
    }
  }, [activeSubTab]);

  // --- Fetch functions ---

  const fetchInventoryStats = async () => {
    try {
      const response = await apiFetch(getApiUrl('/api/inventory/stats'));
      if (response.ok) {
        setInventoryStats(await response.json());
      }
    } catch (error) {
      console.error('Error fetching inventory stats:', error);
    }
  };

  const fetchMaterials = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page_size: '100' });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (typeFilter) params.set('item_type', typeFilter);
      const response = await apiFetch(getApiUrl(`/api/inventory?${params}`));
      if (response.ok) {
        const data = await response.json();
        setMaterials(data.items || []);
      }
    } catch (error) {
      console.error('Error fetching materials:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMaterialTransactions = async (materialId) => {
    try {
      setLoadingTransactions(true);
      const response = await apiFetch(getApiUrl(`/api/inventory/${materialId}/transactions?page_size=100`));
      if (response.ok) {
        const data = await response.json();
        setTransactions(data.transactions || []);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const fetchPutAwayItems = async () => {
    try {
      setLoadingPutAwayItems(true);
      const response = await apiFetch(getApiUrl('/api/put-away/pending?limit=100'));
      if (response.ok) {
        const data = await response.json();
        setPutAwayItems(data || []);
      }
    } catch (error) {
      console.error('Error fetching put-away items:', error);
      setPutAwayItems([]);
    } finally {
      setLoadingPutAwayItems(false);
    }
  };

  const fetchWarehouseLocations = async () => {
    try {
      const response = await apiFetch(getApiUrl('/api/warehouse-locations?is_active=true&limit=200'));
      if (response.ok) {
        const data = await response.json();
        setWarehouseLocations(data || []);
      }
    } catch (error) {
      console.error('Error fetching warehouse locations:', error);
      setWarehouseLocations([]);
    }
  };

  const fetchLocationInventory = async (locationCode = '') => {
    try {
      setLoadingLocationInventory(true);
      const url = locationCode
        ? `/api/warehouse/stock-by-location?location_code=${locationCode}`
        : '/api/warehouse/stock-by-location';
      const response = await apiFetch(getApiUrl(url));
      if (response.ok) {
        const data = await response.json();
        setLocationInventory(data.inventory || []);
      }
    } catch (error) {
      console.error('Error fetching location inventory:', error);
      setLocationInventory([]);
    } finally {
      setLoadingLocationInventory(false);
    }
  };

  // --- Put-away handlers ---

  const handleAssignLocation = (item) => {
    setSelectedPutAwayItem(item);
    setTargetLocation('');
    setAssignmentNotes('');
    setShowAssignLocationModal(true);
  };

  const closeAssignLocationModal = () => {
    setShowAssignLocationModal(false);
    setSelectedPutAwayItem(null);
    setTargetLocation('');
    setAssignmentNotes('');
  };

  const submitLocationAssignment = async () => {
    if (!targetLocation) {
      showWarning(tCommon('ui.invalidInput'), t('inventoryManagement.toasts.selectTargetLocation'));
      return;
    }

    try {
      setSubmittingAssignment(true);
      const response = await apiFetch(getApiUrl(`/api/put-away/${selectedPutAwayItem.grn_item_id}/assign`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          location_code: targetLocation,
          notes: assignmentNotes || null,
          performed_by: user.user_id
        })
      });

      if (response.ok) {
        const result = await response.json();
        closeAssignLocationModal();
        await fetchPutAwayItems(); // Refresh the list first
        showSuccess(t('success.inventory.locationAssigned'), t('success.inventory.locationAssignedMessage', { message: result.message, oldLocation: result.old_location, newLocation: result.new_location }));
      } else {
        const error = await response.json();
        showError(t('errors.inventory.failedToAssignLocation'), error.detail || error.message || t('errors.generic.unknownError'));
      }
    } catch (error) {
      console.error('Error assigning location:', error);
      showError(t('errors.generic.error'), t('errors.inventory.failedToAssignLocationGeneric'));
    } finally {
      setSubmittingAssignment(false);
    }
  };

  // Bulk assignment handlers
  const toggleItemSelection = (grnItemId) => {
    setSelectedItems(prev => {
      if (prev.includes(grnItemId)) {
        return prev.filter(id => id !== grnItemId);
      } else {
        return [...prev, grnItemId];
      }
    });
  };

  const toggleSelectAll = () => {
    if (selectedItems.length === putAwayItems.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(putAwayItems.map(item => item.grn_item_id));
    }
  };

  const handleBulkAssign = () => {
    if (selectedItems.length === 0) {
      showWarning(t('inventoryManagement.toasts.invalidSelection'), t('inventoryManagement.toasts.selectAtLeastOne'));
      return;
    }
    setBulkTargetLocation('');
    setBulkAssignmentNotes('');
    setShowBulkAssignModal(true);
  };

  const closeBulkAssignModal = () => {
    setShowBulkAssignModal(false);
    setBulkTargetLocation('');
    setBulkAssignmentNotes('');
  };

  const submitBulkAssignment = async () => {
    if (!bulkTargetLocation) {
      showWarning(tCommon('ui.invalidInput'), t('inventoryManagement.toasts.selectTargetLocation'));
      return;
    }

    try {
      setSubmittingBulkAssignment(true);
      const response = await apiFetch(getApiUrl('/api/put-away/bulk-assign'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          grn_item_ids: selectedItems,
          location_code: bulkTargetLocation,
          notes: bulkAssignmentNotes || null,
          performed_by: user.user_id
        })
      });

      if (response.ok) {
        const result = await response.json();
        closeBulkAssignModal();
        setSelectedItems([]); // Clear selection
        await fetchPutAwayItems(); // Refresh the list first
        showSuccess(t('success.inventory.bulkAssignmentCompleted'), t('success.inventory.bulkAssignmentMessage', {
          message: result.message,
          successfulCount: result.successful_count,
          failedMessage: result.failed_count > 0 ? t('inventoryManagement.toasts.failedSuffix', { count: result.failed_count }) : ''
        }));
      } else {
        const error = await response.json();
        showError(t('errors.inventory.failedToAssignLocations'), error.detail || error.message || t('errors.generic.unknownError'));
      }
    } catch (error) {
      console.error('Error in bulk assignment:', error);
      showError(t('errors.generic.error'), t('errors.inventory.failedToAssignLocationsGeneric'));
    } finally {
      setSubmittingBulkAssignment(false);
    }
  };

  // General inventory transfer handlers
  const handleInitiateTransfer = (item) => {
    setSelectedInventoryItem(item);
    setTransferFromLocation(item.location);
    setTransferToLocation('');
    setTransferQuantity('');
    setTransferReason('');
    setShowTransferModal(true);
  };

  const closeTransferModal = () => {
    setShowTransferModal(false);
    setSelectedInventoryItem(null);
    setTransferFromLocation('');
    setTransferToLocation('');
    setTransferQuantity('');
    setTransferReason('');
  };

  const submitInventoryTransfer = async () => {
    if (!transferToLocation) {
      showWarning(tCommon('ui.invalidInput'), t('inventoryManagement.toasts.selectDestination'));
      return;
    }
    if (!transferQuantity || parseFloat(transferQuantity) <= 0) {
      showWarning(tCommon('ui.invalidInput'), t('inventoryManagement.toasts.enterValidQuantity'));
      return;
    }
    if (parseFloat(transferQuantity) > parseFloat(selectedInventoryItem.available_quantity)) {
      showWarning(t('inventoryManagement.toasts.invalidQuantity'), t('inventoryManagement.toasts.exceedsAvailable', { available: selectedInventoryItem.available_quantity }));
      return;
    }
    if (!transferReason || transferReason.length < 5) {
      showWarning(tCommon('ui.invalidInput'), t('inventoryManagement.toasts.transferReasonRequired'));
      return;
    }
    if (transferFromLocation === transferToLocation) {
      showWarning(tCommon('ui.invalidInput'), t('inventoryManagement.toasts.sameLocation'));
      return;
    }

    try {
      setSubmittingTransfer(true);
      const response = await apiFetch(getApiUrl('/api/warehouse/transfer-stock'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          inventory_item_id: selectedInventoryItem.inventory_item_id,
          from_location_code: transferFromLocation,
          to_location_code: transferToLocation,
          quantity: transferQuantity,
          reason: transferReason,
          performed_by: user.user_id
        })
      });

      if (response.ok) {
        const result = await response.json();
        closeTransferModal();
        await fetchLocationInventory(selectedLocationFilter);
        showSuccess(t('success.inventory.transferCompleted'), t('success.inventory.transferCompletedMessage', { quantity: result.quantity, unit: selectedInventoryItem.unit_of_measure, itemCode: result.item_code, fromLocation: result.from_location, toLocation: result.to_location }));
      } else {
        const error = await response.json();
        showError(t('errors.inventory.failedToTransferInventory'), error.detail || error.message || t('errors.generic.unknownError'));
      }
    } catch (error) {
      console.error('Error transferring inventory:', error);
      showError(t('errors.generic.error'), t('errors.inventory.failedToTransferInventoryGeneric'));
    } finally {
      setSubmittingTransfer(false);
    }
  };

  // --- Materials inventory handlers ---

  const handleViewHistory = (material) => {
    setSelectedMaterial(material);
    setShowHistoryModal(true);
    fetchMaterialTransactions(material.item_id);
  };

  const closeHistoryModal = () => {
    setShowHistoryModal(false);
    setSelectedMaterial(null);
    setTransactions([]);
  };

  const handleStockAdjustment = async (material) => {
    setSelectedMaterial(material);
    setShowAdjustmentModal(true);
    setAdjustmentType('INCREASE');
    setAdjustmentQuantity('');
    setAdjustmentReason('');
    // Default INCREASE location to the item's configured default location
    // Default to the item's configured default location; refined below to where it is actually stocked
    setAdjustmentLocation(material.default_location || material.location || '');
    setAdjustmentStockLocations([]);

    // Always fetch both in parallel when opening the modal so the dropdowns
    // are populated regardless of which subtab the user came from.
    try {
      const [stockRes, locRes] = await Promise.all([
        apiFetch(getApiUrl(`/api/inventory/${material.item_id}/stock-levels`)),
        apiFetch(getApiUrl('/api/warehouse-locations?is_active=true&limit=200')),
      ]);
      let stockLocs = [];
      let whLocs = [];
      if (stockRes.ok) { stockLocs = await stockRes.json(); setAdjustmentStockLocations(stockLocs); }
      if (locRes.ok) { whLocs = await locRes.json(); setWarehouseLocations(whLocs); }
      // Only keep a default that is a configured location; otherwise use where the item is stocked
      const configured = new Set(whLocs.map(l => l.location_code));
      setAdjustmentLocation(prev =>
        (prev && configured.has(prev)) ? prev
          : (stockLocs[0]?.location || whLocs[0]?.location_code || '')
      );
    } catch (err) {
      console.error('Error fetching adjustment data:', err);
    }
  };

  const closeAdjustmentModal = () => {
    setShowAdjustmentModal(false);
    setSelectedMaterial(null);
    setAdjustmentType('INCREASE');
    setAdjustmentQuantity('');
    setAdjustmentReason('');
    setAdjustmentLocation('');
    setAdjustmentStockLocations([]);
  };

  const submitStockAdjustment = async () => {
    if (!selectedMaterial) {
      showError(t('errors.generic.error'), t('errors.stockAdjustment.noMaterialSelected'));
      return;
    }

    if (!adjustmentQuantity || !adjustmentReason.trim() || !adjustmentLocation) {
      showError(t('errors.stockAdjustment.validationError'), t('errors.stockAdjustment.fillAllFields'));
      return;
    }

    // A zero or negative quantity is not an adjustment (the server rejects it too)
    const quantity = parseFloat(adjustmentQuantity);
    if (!(quantity > 0)) {
      showError(t('errors.stockAdjustment.validationError'), t('stockAdjustment.messages.quantityPositive', 'Quantity must be greater than 0'));
      return;
    }

    const adjustmentData = {
      inventory_item_id: selectedMaterial.item_id,
      adjustment_type: adjustmentType,
      quantity,
      reason: adjustmentReason.trim(),
      location: adjustmentLocation
    };

    setIsSubmittingAdjustment(true);
    try {
      const response = await apiFetch(getApiUrl('/api/inventory/stock-adjustment'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(adjustmentData),
      });

      if (response.ok) {
        const result = await response.json();
        showSuccess(t('success.stockAdjustment.success'), t('success.stockAdjustment.adjustmentCompleted', { balanceAfter: result.balance_after }));
        closeAdjustmentModal();
        // Refresh materials list
        fetchMaterials();
      } else {
        await showApiError(response, showError, tCommon, t('stockAdjustment.messages.adjustmentError', 'Failed to create stock adjustment'));
      }
    } catch (error) {
      console.error('Error creating stock adjustment:', error);
      showError(t('errors.stockAdjustment.networkError'), t('errors.stockAdjustment.unableToCreateAdjustment'));
    } finally {
      setIsSubmittingAdjustment(false);
    }
  };

  // --- Filtered materials computation ---

  // stock_status comes from the API: same rule as F5 and as the overview cards (inventory/service.py STOCK_STATUS_SQL)
  const getStockState = (material) => {
    if (material.stock_status === 'OUT_OF_STOCK') return 'out';
    if (material.stock_status === 'REORDER' || material.stock_status === 'CRITICAL') return 'low';
    if (material.stock_status === 'OVERSTOCKED') return 'over';
    return 'normal';
  };

  const filteredMaterials = materials.filter(material => {
    // Server handles search & type filter; only client-filter stock status
    const state = getStockState(material);
    let matchesStock = true;
    if (stockFilter === 'low') {
      matchesStock = state === 'low';
    } else if (stockFilter === 'out') {
      matchesStock = state === 'out';
    } else if (stockFilter === 'normal') {
      matchesStock = state === 'normal' || state === 'over';
    }

    return matchesStock;
  });

  // --- Render functions ---

  const renderMaterialsInventory = () => (
    <div className="warehouse-section">
      {/* Search and Filter Controls */}
      <div className="inventory-controls">
        <div className="search-bar">
          <input
            type="text"
            placeholder={t('common.fields.searchMaterials', 'Search materials by code or name...')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filters">
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">{t('common.filters.allTypes', 'All Types')}</option>
            <option value="RAW">{t('common.filters.rawMaterials', 'Raw Materials')}</option>
            <option value="INK">{t('common.filters.ink', 'Ink')}</option>
            <option value="PACKAGING">{t('common.filters.packaging', 'Packaging')}</option>
            <option value="CONSUMABLE">{t('common.filters.consumables', 'Consumables')}</option>
            <option value="FINISHED_GOOD">{t('common.filters.finishedGoods', 'Finished Goods')}</option>
          </select>
          <select
            value={stockFilter}
            onChange={(e) => setStockFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">{t('common.filters.allStockLevels', 'All Stock Levels')}</option>
            <option value="low">{t('common.filters.lowStock', 'Low Stock')}</option>
            <option value="out">{t('common.filters.outOfStock', 'Out of Stock')}</option>
            <option value="normal">{t('common.filters.normalStock', 'Normal Stock')}</option>
          </select>
          <button
            className="btn-action btn-secondary"
            onClick={() => {
              setSearchTerm('');
              setTypeFilter('');
              setStockFilter('');
            }}
          >
            {t('common.buttons.clearFilters', 'Clear Filters')}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading-state">{t('common.messages.loading', 'Loading...')}</div>
      ) : (
        <>
          <div className="results-info">
            {t('common.messages.showingMaterials', 'Showing {{filtered}} of {{total}} materials', {
              filtered: filteredMaterials.length,
              total: materials.length
            })}
          </div>
          <div className="warehouse-table-container">
          <table className="warehouse-table">
            <thead>
              <tr>
                <th>{t('common.headers.materialCode', 'Material Code')}</th>
                <th>{t('common.headers.materialName', 'Material Name')}</th>
                <th>{t('common.headers.type', 'Type')}</th>
                <th>{t('common.headers.currentStock', 'Current Stock')}</th>
                <th>{t('common.headers.unit', 'Unit')}</th>
                <th style={{ textAlign: 'right' }}>{t('common.headers.avgCost', 'Avg Cost')}</th>
                <th style={{ textAlign: 'right' }}>{t('common.headers.lastCost', 'Last Cost')}</th>
                <th>{t('common.headers.reorderPoint', 'Reorder Point')}</th>
                <th>{t('common.headers.maxStock', 'Max Stock')}</th>
                <th>{t('common.headers.status', 'Status')}</th>
                <th>{t('common.headers.location', 'Location')}</th>
                <th>{t('common.headers.actions', 'Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {materials.length === 0 ? (
                <tr>
                  <td colSpan="12" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                    {t('common.messages.noMaterialsFound', 'No materials found in inventory')}
                  </td>
                </tr>
              ) : (
                filteredMaterials.map(material => {
                  const currentStock = material.current_stock || 0;
                  const reorderPoint = Number(material.reorder_point || 0);
                  const maxStock = Number(material.maximum_stock || 0);

                  let stockStatus = t('inventoryManagement.stockStatus.normal');
                  let statusClass = 'status-verified';
                  const stockState = getStockState(material);

                  if (stockState === 'out') {
                    stockStatus = t('inventoryManagement.stockStatus.outOfStock');
                    statusClass = 'status-out-of-stock';
                  } else if (stockState === 'low') {
                    stockStatus = t('inventoryManagement.stockStatus.lowStock');
                    statusClass = 'status-in-transit';
                  } else if (stockState === 'over') {
                    stockStatus = t('inventoryManagement.stockStatus.overstocked');
                    statusClass = 'status-processing';
                  }

                  return (
                    <tr key={material.item_id}>
                      <td>
                        <button
                          className="entity-link"
                          onClick={() => { setViewItemId(material.item_id); setItemEditMode(false); }}
                          title={t('inventoryManagement.labels.openItem', { code: material.item_code })}
                        >
                          {material.item_code} →
                        </button>
                      </td>
                      <td>{material.item_name}</td>
                      <td>{material.item_type || tCommon('ui.notAvailable')}</td>
                      <td style={{ fontWeight: 600, color: stockState === 'out' ? 'var(--error)' : stockState === 'low' ? 'var(--warning)' : 'inherit' }}>
                        {currentStock.toLocaleString()}
                      </td>
                      <td>{material.unit_of_measure || 'pcs'}</td>
                      <td style={{ textAlign: 'right' }}>${(material.average_cost || 0).toFixed(4)}</td>
                      <td style={{ textAlign: 'right' }}>{material.last_purchase_cost != null ? `$${material.last_purchase_cost.toFixed(4)}` : '-'}</td>
                      <td>{reorderPoint > 0 ? reorderPoint.toLocaleString() : '-'}</td>
                      <td>{maxStock > 0 ? maxStock.toLocaleString() : '-'}</td>
                      <td><span className={`status-badge ${statusClass}`}>{stockStatus}</span></td>
                      <td>{material.warehouse_location || t('common.fields.notAssigned', 'Not Assigned')}</td>
                      <td>
                        <button className="btn-action btn-secondary" onClick={() => handleViewHistory(material)}>
                          {t('common.buttons.history', 'History')}
                        </button>
                        <button className="btn-action btn-primary" onClick={() => handleStockAdjustment(material)}>
                          {t('common.buttons.adjustment', 'Adjustment')}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        </>
      )}
      {!loading && (
        <div className="inventory-summary">
          <div className="summary-card">
            <span className="summary-label">{t('inventoryManagement.summary.totalMaterials')}:</span>
            <span className="summary-value">{inventoryStats.total_materials}</span>
          </div>
          <div className="summary-card">
            <span className="summary-label">{t('inventoryManagement.summary.lowStockItems')}:</span>
            <span className="summary-value" style={{ color: 'var(--warning)' }}>
              {inventoryStats.low_stock}
            </span>
          </div>
          <div className="summary-card">
            <span className="summary-label">{t('inventoryManagement.summary.outOfStock')}:</span>
            <span className="summary-value" style={{ color: 'var(--error)' }}>
              {inventoryStats.out_of_stock}
            </span>
          </div>
          <div className="summary-card">
            <span className="summary-label">{t('inventoryManagement.summary.overstocked')}:</span>
            <span className="summary-value" style={{ color: 'var(--info)' }}>
              {inventoryStats.overstocked}
            </span>
          </div>
        </div>
      )}
    </div>
  );

  const renderPutAway = () => (
    <div className="warehouse-section">

      {/* Tab Navigation */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', borderBottom: '2px solid var(--border)' }}>
        <button
          style={{
            padding: '0.75rem 1.5rem',
            background: transferTab === 'putaway' ? 'var(--bg-secondary)' : 'transparent',
            border: 'none',
            borderBottom: transferTab === 'putaway' ? '2px solid var(--primary)' : '2px solid transparent',
            color: transferTab === 'putaway' ? 'var(--primary)' : 'var(--text-secondary)',
            fontWeight: transferTab === 'putaway' ? '600' : '400',
            cursor: 'pointer',
            marginBottom: '-2px'
          }}
          onClick={() => setTransferTab('putaway')}
        >
          📍 {t('inventoryManagement.tabs.putAway')}
        </button>
        <button
          style={{
            padding: '0.75rem 1.5rem',
            background: transferTab === 'general' ? 'var(--bg-secondary)' : 'transparent',
            border: 'none',
            borderBottom: transferTab === 'general' ? '2px solid var(--primary)' : '2px solid transparent',
            color: transferTab === 'general' ? 'var(--primary)' : 'var(--text-secondary)',
            fontWeight: transferTab === 'general' ? '600' : '400',
            cursor: 'pointer',
            marginBottom: '-2px'
          }}
          onClick={() => {
            setTransferTab('general');
            fetchLocationInventory(selectedLocationFilter);
          }}
        >
          🔄 {t('inventoryManagement.tabs.generalTransfers')}
        </button>
      </div>

      {/* Put-Away Tab Content */}
      {transferTab === 'putaway' && (
        <>
          <div className="action-buttons">
            <button className="btn-action btn-success" onClick={fetchPutAwayItems}>🔄 {tCommon('ui.refresh')}</button>
            {selectedItems.length > 0 && (
              <button className="btn-action btn-primary" onClick={handleBulkAssign}>
                📦 {t('inventoryManagement.buttons.bulkAssign', { count: selectedItems.length })}
              </button>
            )}
          </div>
          {loadingPutAwayItems ? (
            <div className="loading-state">{t('inventoryManagement.loading.putAway')}</div>
          ) : (
            <div className="warehouse-table-container">
              <table className="warehouse-table">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>
                      <input
                        type="checkbox"
                        checked={putAwayItems.length > 0 && selectedItems.length === putAwayItems.length}
                        onChange={toggleSelectAll}
                        style={{ cursor: 'pointer' }}
                      />
                    </th>
                    <th>{t('inventoryManagement.columns.grnNumber')}</th>
                    <th>{tCommon('ui.material')}</th>
                    <th>{t('inventoryManagement.columns.supplier')}</th>
                    <th>{tCommon('ui.quantity')}</th>
                    <th>{t('inventoryManagement.columns.batch')}</th>
                    <th>{t('inventoryManagement.columns.receiptDate')}</th>
                    <th>{t('inventoryManagement.columns.daysWaiting')}</th>
                    <th>{t('inventoryManagement.columns.currentLocation')}</th>
                    <th>{tCommon('ui.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {putAwayItems.length === 0 ? (
                    <tr>
                      <td colSpan="10" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                        {t('inventoryManagement.empty.noPutAway')}
                        <p style={{ fontSize: '0.9em', marginTop: '0.5rem' }}>
                          {t('inventoryManagement.empty.noPutAwayHint')}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    putAwayItems.map(item => {
                      const daysWaiting = item.days_waiting || 0;
                      let daysClass = 'status-success';
                      if (daysWaiting > 7) daysClass = 'status-error';
                      else if (daysWaiting > 2) daysClass = 'status-warning';

                      return (
                        <tr key={item.grn_item_id}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedItems.includes(item.grn_item_id)}
                              onChange={() => toggleItemSelection(item.grn_item_id)}
                              style={{ cursor: 'pointer' }}
                            />
                          </td>
                          <td><strong>{item.grn_number}</strong></td>
                          <td>
                            <strong>{item.item_code}</strong><br />
                            <span style={{ fontSize: '0.85em', color: 'var(--text-secondary)' }}>{item.item_name}</span>
                          </td>
                          <td>{item.supplier_name}</td>
                          <td>{parseFloat(item.quantity_received).toLocaleString()} {item.unit_of_measure || ''}</td>
                          <td>{item.batch_number || '-'}</td>
                          <td>{formatAppDate(item.receipt_date)}</td>
                          <td><span className={`status-badge ${daysClass}`}>{t('inventoryManagement.labels.days', { count: daysWaiting })}</span></td>
                          <td><strong>{item.current_location}</strong></td>
                          <td>
                            <button
                              className="btn-action btn-primary"
                              onClick={() => handleAssignLocation(item)}
                            >
                              📍 {t('inventoryManagement.buttons.assignLocation')}
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* General Transfers Tab Content */}
      {transferTab === 'general' && (
        <>
          <div className="action-buttons">
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <LocationSelect
                value={selectedLocationFilter}
                onChange={(value) => {
                  setSelectedLocationFilter(value);
                  fetchLocationInventory(value);
                }}
                name="location-filter"
                placeholder={t('inventoryManagement.labels.allLocations')}
                className=""
              />
              <button className="btn-action btn-success" onClick={() => fetchLocationInventory(selectedLocationFilter)}>
                🔄 {tCommon('ui.refresh')}
              </button>
            </div>
          </div>
          {loadingLocationInventory ? (
            <div className="loading-state">{t('inventoryManagement.loading.locationInventory')}</div>
          ) : (
            <div className="warehouse-table-container">
              <table className="warehouse-table">
                <thead>
                  <tr>
                    <th>{t('inventoryManagement.columns.itemCode')}</th>
                    <th>{t('inventoryManagement.columns.itemName')}</th>
                    <th>{tCommon('ui.type')}</th>
                    <th>{t('inventoryManagement.columns.location')}</th>
                    <th>{t('inventoryManagement.columns.availableQty')}</th>
                    <th>{t('inventoryManagement.columns.reservedQty')}</th>
                    <th>{t('inventoryManagement.columns.totalQty')}</th>
                    <th>{t('inventoryManagement.columns.lastUpdated')}</th>
                    <th>{tCommon('ui.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {locationInventory.length === 0 ? (
                    <tr>
                      <td colSpan="9" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                        {selectedLocationFilter
                          ? t('inventoryManagement.empty.noInventoryAt', { location: selectedLocationFilter })
                          : t('inventoryManagement.empty.noInventoryAnywhere')}
                        <p style={{ fontSize: '0.9em', marginTop: '0.5rem' }}>
                          {t('inventoryManagement.empty.noInventoryHint')}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    locationInventory.map((item, index) => (
                      <tr key={`${item.inventory_item_id}-${item.location}-${index}`}>
                        <td><strong>{item.item_code}</strong></td>
                        <td>{item.item_name}</td>
                        <td>{item.item_type || '-'}</td>
                        <td><strong>{item.location}</strong></td>
                        <td>{parseFloat(item.available_quantity).toLocaleString()} {item.unit_of_measure || ''}</td>
                        <td>{parseFloat(item.reserved_quantity || 0).toLocaleString()}</td>
                        <td>{parseFloat(item.total_quantity).toLocaleString()} {item.unit_of_measure || ''}</td>
                        <td>{item.last_updated ? formatAppDate(item.last_updated, 'datetime') : '-'}</td>
                        <td>
                          <button
                            className="btn-action btn-primary"
                            onClick={() => handleInitiateTransfer(item)}
                            disabled={parseFloat(item.available_quantity) <= 0}
                          >
                            🔄 {t('inventoryManagement.buttons.transfer')}
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );

  // --- Warehouse Locations CRUD ---

  const handleSelectLocation = (locationId) => {
    setSelectedLocationId(locationId);
    setLocationsViewMode('view');
  };

  const handleLocationsBack = () => {
    setSelectedLocationId(null);
    setLocationsViewMode('list');
  };

  const handleLocationsNew = () => {
    setSelectedLocationId(null);
    setLocationsViewMode('create');
  };

  const handleLocationsEdit = () => {
    setLocationsViewMode('edit');
  };

  const handleLocationsCancel = () => {
    if (locationsViewMode === 'create') {
      setLocationsViewMode('list');
      setSelectedLocationId(null);
    } else {
      setLocationsViewMode('view');
    }
  };

  const handleLocationsSave = () => {
    if (locationsViewRef.current) {
      locationsViewRef.current.handleSave();
    }
  };

  const handleLocationsDelete = () => {
    if (locationsViewRef.current) {
      locationsViewRef.current.handleDelete();
    }
  };

  const handleLocationsRefresh = () => {
    if (locationsListRef.current) {
      locationsListRef.current.fetchLocations();
    }
  };

  const handleLocationCreated = (locationId) => {
    setSelectedLocationId(locationId);
    setLocationsViewMode('view');
    if (locationsListRef.current) {
      locationsListRef.current.fetchLocations();
    }
  };

  const handleLocationSaveComplete = () => {
    setLocationsViewMode('view');
    if (locationsListRef.current) {
      locationsListRef.current.fetchLocations();
    }
  };

  const renderLocationsToolbar = () => {
    if (locationsViewMode === 'list') {
      return (
        <div className="locations-toolbar">
          <button className="btn-primary" onClick={handleLocationsNew}>
            + {t('warehouseLocations.newLocation')}
          </button>
          <button className="btn-secondary" onClick={handleLocationsRefresh}>
            {t('common.buttons.refresh')}
          </button>
        </div>
      );
    } else if (locationsViewMode === 'create' || locationsViewMode === 'edit') {
      return (
        <div className="locations-toolbar">
          <button className="btn-primary" onClick={handleLocationsSave}>
            {t('common.buttons.save')}
          </button>
          <button className="btn-secondary" onClick={handleLocationsCancel}>
            {t('common.buttons.cancel')}
          </button>
        </div>
      );
    } else {
      return (
        <div className="locations-toolbar">
          <button className="btn-primary" onClick={handleLocationsEdit}>
            {t('common.buttons.edit')}
          </button>
          <button className="btn-secondary" style={{ color: 'var(--status-error, #f44336)' }} onClick={handleLocationsDelete}>
            {t('common.buttons.delete')}
          </button>
          <button className="btn-secondary" onClick={handleLocationsBack}>
            {t('common.buttons.back')}
          </button>
        </div>
      );
    }
  };

  const renderLocations = () => (
    <div className="locations-management">
      {renderLocationsToolbar()}
      <ResizablePanels
        leftPanel={
          <WarehouseLocationsList
            ref={locationsListRef}
            onSelectLocation={handleSelectLocation}
            selectedLocationId={selectedLocationId}
            onNew={handleLocationsNew}
          />
        }
        rightPanel={
          locationsViewMode !== 'list' ? (
            <ViewWarehouseLocation
              ref={locationsViewRef}
              locationId={selectedLocationId}
              editMode={locationsViewMode === 'edit'}
              createMode={locationsViewMode === 'create'}
              onBack={handleLocationsBack}
              onEdit={handleLocationsEdit}
              onSave={handleLocationSaveComplete}
              onCancel={handleLocationsCancel}
              onLocationCreated={handleLocationCreated}
            />
          ) : null
        }
        leftCollapsed={locationsViewMode !== 'list'}
        defaultLeftWidth={40}
        minLeftWidth={30}
        maxLeftWidth={70}
      />
    </div>
  );

  // --- Main render ---

  return (
    <>
      {activeSubTab === 'inventory' && (viewItemId ? (
        <ViewInventoryItem
          key={viewItemId}
          itemId={viewItemId}
          editMode={itemEditMode}
          onBack={closeItemView}
          onEdit={() => setItemEditMode(true)}
          onCancel={() => setItemEditMode(false)}
          onSave={() => { setItemEditMode(false); fetchMaterials(); }}
        />
      ) : renderMaterialsInventory())}
      {activeSubTab === 'storage' && renderPutAway()}
      {activeSubTab === 'locations' && renderLocations()}
      {activeSubTab === 'cycle-counts' && <CycleCountsManager />}
      {activeSubTab === 'formulations' && <FormulationsManager />}
      {activeSubTab === 'extrusions' && <ExtrusionsManager />}

      {/* Material Transaction History Modal */}
      {showHistoryModal && (
        <div className="modal-overlay" onClick={closeHistoryModal}>
          <div className="modal-content history-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📜 {t('common.headers.transactionHistory', 'Transaction History')}</h2>
              <button className="modal-close" onClick={closeHistoryModal}>✕</button>
            </div>

            {selectedMaterial && (
              <div className="modal-material-info">
                <div className="material-info-row">
                  <span className="info-label">{t('common.fields.material', 'Material')}:</span>
                  <span className="info-value"><strong>{selectedMaterial.item_code}</strong> - {selectedMaterial.item_name}</span>
                </div>
                <div className="material-info-row">
                  <span className="info-label">{t('common.headers.type', 'Type')}:</span>
                  <span className="info-value">{selectedMaterial.item_type}</span>
                  <span className="info-label">{t('common.headers.currentStock', 'Current Stock')}:</span>
                  <span className="info-value"><strong>{selectedMaterial.current_stock || 0}</strong> {selectedMaterial.unit_of_measure}</span>
                </div>
              </div>
            )}

            <div className="modal-body">
              {loadingTransactions ? (
                <div className="loading-state">{t('common.messages.loadingTransactions', 'Loading transaction history...')}</div>
              ) : transactions.length === 0 ? (
                <div className="no-data">
                  <p>{t('common.messages.noTransactionHistory', 'No transaction history found for this material.')}</p>
                </div>
              ) : (
                <div className="transactions-table-container">
                  <table className="warehouse-table">
                    <thead>
                      <tr>
                        <th>{t('common.headers.date', 'Date')}</th>
                        <th>{t('common.headers.type', 'Type')}</th>
                        <th>{t('common.headers.subtype', 'Subtype')}</th>
                        <th>{t('common.headers.quantity', 'Quantity')}</th>
                        <th style={{ textAlign: 'right' }}>{t('common.headers.unitCost', 'Unit Cost')}</th>
                        <th style={{ textAlign: 'right' }}>{t('common.headers.totalValue', 'Total Value')}</th>
                        <th>{t('common.headers.balanceBefore', 'Balance Before')}</th>
                        <th>{t('common.headers.balanceAfter', 'Balance After')}</th>
                        <th>{t('common.headers.location', 'Location')}</th>
                        <th>{t('common.headers.reference', 'Reference')}</th>
                        <th>{t('common.headers.reason', 'Reason')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((txn) => {
                        const isIncoming = txn.transaction_type === 'IN';
                        const isOutgoing = txn.transaction_type === 'OUT';
                        const isAdjustment = txn.transaction_type === 'ADJUST';
                        const isReturn = txn.transaction_type === 'RETURN';

                        return (
                          <tr key={txn.transaction_id}>
                            <td>{formatAppDate(txn.transaction_date, 'datetime')}</td>
                            <td>
                              <span className={`transaction-type ${isIncoming || isReturn ? 'type-in' : isOutgoing ? 'type-out' : 'type-adjust'}`}>
                                {txn.transaction_type}
                              </span>
                            </td>
                            <td>{txn.transaction_subtype || '-'}</td>
                            <td style={{
                              fontWeight: 600,
                              color: isIncoming || isReturn ? 'var(--success)' : isOutgoing ? 'var(--error)' : 'var(--warning)'
                            }}>
                              {isIncoming || isReturn ? '+' : isOutgoing ? '-' : ''}{txn.quantity} {selectedMaterial.unit_of_measure}
                            </td>
                            <td style={{ textAlign: 'right' }}>{txn.unit_cost ? `$${Number(txn.unit_cost).toFixed(4)}` : '-'}</td>
                            <td style={{ textAlign: 'right' }}>
                              {txn.total_value
                                ? `$${Number(txn.total_value).toFixed(2)}`
                                : txn.running_average_cost
                                  ? `$${(Number(txn.quantity) * Number(txn.running_average_cost)).toFixed(2)}`
                                  : '-'}
                            </td>
                            <td>{txn.balance_before != null ? Number(txn.balance_before).toLocaleString() : 0}</td>
                            <td><strong>{txn.balance_after != null ? Number(txn.balance_after).toLocaleString() : 0}</strong></td>
                            <td>
                              {txn.location_from && txn.location_to
                                ? `${txn.location_from} → ${txn.location_to}`
                                : txn.location_to || txn.location_from || '-'}
                            </td>
                            <td>
                              {txn.reference_type && txn.reference_id
                                ? `${txn.reference_type} #${txn.reference_id}`
                                : '-'}
                            </td>
                            <td>{txn.reason || '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn-action btn-secondary" onClick={closeHistoryModal}>
                {t('common.buttons.close', 'Close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stock Adjustment Modal */}
      {showAdjustmentModal && (
        <div className="modal-overlay" onClick={closeAdjustmentModal}>
          <div className="modal-content adjustment-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>⚖️ {t('stockAdjustment.title')}</h2>
              <button className="modal-close" onClick={closeAdjustmentModal}>✕</button>
            </div>

            {selectedMaterial && (
              <div className="modal-material-info">
                <div className="material-info-row">
                  <span className="info-label">{tCommon('ui.material')}:</span>
                  <span className="info-value"><strong>{selectedMaterial.item_code}</strong> - {selectedMaterial.item_name}</span>
                </div>
                <div className="material-info-row">
                  <span className="info-label">{t('inventoryManagement.labels.currentStock')}:</span>
                  <span className="info-value"><strong>{selectedMaterial.current_stock || 0}</strong> {selectedMaterial.unit_of_measure}</span>
                  <span className="info-label">{t('inventoryManagement.labels.averageCost')}:</span>
                  <span className="info-value">${selectedMaterial.average_cost?.toFixed(2) || '0.00'}</span>
                </div>
              </div>
            )}

            <div className="modal-body">
              <form id="stock-adjustment-form" onSubmit={(e) => { e.preventDefault(); submitStockAdjustment(); }}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="adjustment-type">{t('inventoryManagement.adjustment.typeLabel')}</label>
                    <select
                      id="adjustment-type"
                      value={adjustmentType}
                      onChange={(e) => {
                        const newType = e.target.value;
                        setAdjustmentType(newType);
                        // Re-default location when switching types
                        if (newType === 'DECREASE') {
                          setAdjustmentLocation(adjustmentStockLocations.length > 0 ? adjustmentStockLocations[0].location : '');
                        } else {
                          setAdjustmentLocation(
                            (adjustmentStockLocations.length > 0 ? adjustmentStockLocations[0].location : '') ||
                            selectedMaterial?.default_location ||
                            (warehouseLocations.length > 0 ? warehouseLocations[0].location_code : '')
                          );
                        }
                      }}
                      required
                    >
                      <option value="INCREASE">{t('inventoryManagement.adjustment.increase')}</option>
                      <option value="DECREASE">{t('inventoryManagement.adjustment.decrease')}</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="adjustment-quantity">{t('inventoryManagement.adjustment.quantityLabel')}</label>
                    <input
                      type="number"
                      id="adjustment-quantity"
                      value={adjustmentQuantity}
                      onChange={(e) => setAdjustmentQuantity(e.target.value)}
                      min="0.001"
                      step="0.001"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group full-width">
                    <label htmlFor="adjustment-location">{t('inventoryManagement.adjustment.locationLabel')}</label>
                    {adjustmentType === 'DECREASE'
                      ? /* DECREASE: only locations where this item has stock */
                        <select
                          id="adjustment-location"
                          value={adjustmentLocation}
                          onChange={(e) => setAdjustmentLocation(e.target.value)}
                          required
                        >
                          {adjustmentStockLocations.length > 0
                            ? adjustmentStockLocations.map(l => (
                                <option key={l.location} value={l.location}>
                                  {l.location} — {t('inventoryManagement.adjustment.available', { quantity: l.available_quantity, unit: selectedMaterial?.unit_of_measure })}
                                </option>
                              ))
                            : <option value="">— {t('inventoryManagement.adjustment.noStockFound')} —</option>
                          }
                        </select>
                      : /* INCREASE: all warehouse locations with current stock on hand */
                        <select
                          id="adjustment-location"
                          value={adjustmentLocation}
                          onChange={(e) => setAdjustmentLocation(e.target.value)}
                          required
                        >
                          {warehouseLocations.length > 0
                            ? warehouseLocations.map(l => {
                                const onHand = adjustmentStockLocations.find(s => s.location === l.location_code);
                                return (
                                  <option key={l.location_code} value={l.location_code}>
                                    {l.location_code}{onHand
                                      ? ` — ${t('inventoryManagement.adjustment.onHand', { quantity: onHand.available_quantity, unit: selectedMaterial?.unit_of_measure })}`
                                      : ` — ${l.location_name}`}
                                  </option>
                                );
                              })
                            : <option value="">— {t('inventoryManagement.adjustment.noLocationsConfigured')} —</option>
                          }
                        </select>
                    }
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="adjustment-reason">{t('inventoryManagement.adjustment.reasonLabel')}</label>
                  <textarea
                    id="adjustment-reason"
                    value={adjustmentReason}
                    onChange={(e) => setAdjustmentReason(e.target.value)}
                    placeholder={t('placeholders.adjustmentReason')}
                    rows="3"
                    required
                  />
                </div>
              </form>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn-action btn-secondary" onClick={closeAdjustmentModal}>{tCommon('ui.cancel')}</button>
              {/* Submits the form above (it lives outside it) so the browser validates the fields first */}
              <button
                type="submit"
                form="stock-adjustment-form"
                className="btn-action btn-primary"
                disabled={isSubmittingAdjustment}
              >
                {isSubmittingAdjustment ? t('inventoryManagement.adjustment.creating') : t('inventoryManagement.adjustment.create')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Location Modal */}
      {showAssignLocationModal && selectedPutAwayItem && (
        <div className="modal-overlay" onClick={closeAssignLocationModal}>
          <div className="modal-content history-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2>📍 {t('inventoryManagement.assignModal.title')}</h2>
              <button className="modal-close" onClick={closeAssignLocationModal}>✕</button>
            </div>

            <div className="modal-material-info">
              <div className="material-info-row">
                <span className="info-label">{t('inventoryManagement.labels.grnNumber')}:</span>
                <span className="info-value"><strong>{selectedPutAwayItem.grn_number}</strong></span>
              </div>
              <div className="material-info-row">
                <span className="info-label">{tCommon('ui.material')}:</span>
                <span className="info-value">{selectedPutAwayItem.item_code} - {selectedPutAwayItem.item_name}</span>
              </div>
              <div className="material-info-row">
                <span className="info-label">{tCommon('ui.quantity')}:</span>
                <span className="info-value"><strong>{parseFloat(selectedPutAwayItem.quantity_received).toLocaleString()} {selectedPutAwayItem.unit_of_measure || ''}</strong></span>
                <span className="info-label">{t('inventoryManagement.columns.currentLocation')}:</span>
                <span className="info-value">{selectedPutAwayItem.current_location}</span>
              </div>
              {selectedPutAwayItem.batch_number && (
                <div className="material-info-row">
                  <span className="info-label">{t('inventoryManagement.labels.batchNumber')}:</span>
                  <span className="info-value">{selectedPutAwayItem.batch_number}</span>
                </div>
              )}
            </div>

            <div className="modal-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                    {t('inventoryManagement.assignModal.targetLocation')} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <LocationSelect
                    value={targetLocation}
                    onChange={(value) => setTargetLocation(value)}
                    name="target-location"
                    required
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                    {t('inventoryManagement.assignModal.notesOptional')}
                  </label>
                  <textarea
                    value={assignmentNotes}
                    onChange={(e) => setAssignmentNotes(e.target.value)}
                    placeholder={t('placeholders.locationNotes')}
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      border: '1px solid var(--border)',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: '1rem',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn-action btn-secondary"
                onClick={closeAssignLocationModal}
                disabled={submittingAssignment}
              >
                {tCommon('ui.cancel')}
              </button>
              <button
                className="btn-action btn-success"
                onClick={submitLocationAssignment}
                disabled={submittingAssignment || !targetLocation}
              >
                {submittingAssignment ? t('inventoryManagement.buttons.assigning') : `📍 ${t('inventoryManagement.buttons.assignLocation')}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Assign Location Modal */}
      {showBulkAssignModal && (
        <div className="modal-overlay" onClick={closeBulkAssignModal}>
          <div className="modal-content history-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2>📦 {t('inventoryManagement.bulkModal.title')}</h2>
              <button className="modal-close" onClick={closeBulkAssignModal}>✕</button>
            </div>

            <div className="modal-material-info">
              <div className="material-info-row">
                <span className="info-label">{t('inventoryManagement.bulkModal.selectedItems')}:</span>
                <span className="info-value"><strong>{selectedItems.length}</strong></span>
              </div>
              <div className="material-info-row">
                <span className="info-label">{t('inventoryManagement.bulkModal.action')}:</span>
                <span className="info-value">{t('inventoryManagement.bulkModal.actionDescription')}</span>
              </div>
            </div>

            <div className="modal-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                    {t('inventoryManagement.assignModal.targetLocation')} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <LocationSelect
                    value={bulkTargetLocation}
                    onChange={(value) => setBulkTargetLocation(value)}
                    name="bulk-target-location"
                    required
                  />
                  <p style={{ fontSize: '0.85em', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                    {t('inventoryManagement.bulkModal.willBeMoved', { count: selectedItems.length })}
                  </p>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                    {t('inventoryManagement.assignModal.notesOptional')}
                  </label>
                  <textarea
                    value={bulkAssignmentNotes}
                    onChange={(e) => setBulkAssignmentNotes(e.target.value)}
                    placeholder={t('placeholders.bulkAssignmentNotes')}
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      border: '1px solid var(--border)',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: '1rem',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn-action btn-secondary"
                onClick={closeBulkAssignModal}
                disabled={submittingBulkAssignment}
              >
                {tCommon('ui.cancel')}
              </button>
              <button
                className="btn-action btn-success"
                onClick={submitBulkAssignment}
                disabled={submittingBulkAssignment || !bulkTargetLocation}
              >
                {submittingBulkAssignment ? t('inventoryManagement.buttons.assigning') : `📦 ${t('inventoryManagement.buttons.assignItems', { count: selectedItems.length })}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Inventory Transfer Modal */}
      {showTransferModal && selectedInventoryItem && (
        <div className="modal-overlay" onClick={closeTransferModal}>
          <div className="modal-content history-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2>🔄 {t('inventoryManagement.transferModal.title')}</h2>
              <button className="modal-close" onClick={closeTransferModal}>✕</button>
            </div>

            <div className="modal-material-info">
              <div className="material-info-row">
                <span className="info-label">{t('inventoryManagement.transferModal.item')}:</span>
                <span className="info-value"><strong>{selectedInventoryItem.item_code}</strong> - {selectedInventoryItem.item_name}</span>
              </div>
              <div className="material-info-row">
                <span className="info-label">{tCommon('ui.type')}:</span>
                <span className="info-value">{selectedInventoryItem.item_type || '-'}</span>
                <span className="info-label">{t('inventoryManagement.columns.currentLocation')}:</span>
                <span className="info-value"><strong>{selectedInventoryItem.location}</strong></span>
              </div>
              <div className="material-info-row">
                <span className="info-label">{t('inventoryManagement.transferModal.availableQuantity')}:</span>
                <span className="info-value"><strong>{parseFloat(selectedInventoryItem.available_quantity).toLocaleString()} {selectedInventoryItem.unit_of_measure || ''}</strong></span>
              </div>
            </div>

            <div className="modal-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                    {t('inventoryManagement.transferModal.fromLocation')}
                  </label>
                  <input
                    type="text"
                    value={transferFromLocation}
                    disabled
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      border: '1px solid var(--border)',
                      background: 'var(--bg-tertiary)',
                      color: 'var(--text-secondary)',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                    {t('inventoryManagement.transferModal.toLocation')} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <LocationSelect
                    value={transferToLocation}
                    onChange={(value) => setTransferToLocation(value)}
                    name="transfer-to-location"
                    required
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                    {t('inventoryManagement.transferModal.quantityToTransfer')} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="number"
                    value={transferQuantity}
                    onChange={(e) => setTransferQuantity(e.target.value)}
                    placeholder={t('inventoryManagement.transferModal.maxPlaceholder', { max: selectedInventoryItem.available_quantity })}
                    min="0"
                    max={selectedInventoryItem.available_quantity}
                    step="0.01"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      border: '1px solid var(--border)',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: '1rem'
                    }}
                  />
                  <p style={{ fontSize: '0.85em', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                    {t('inventoryManagement.transferModal.availableHint', { quantity: parseFloat(selectedInventoryItem.available_quantity).toLocaleString(), unit: selectedInventoryItem.unit_of_measure || '' })}
                  </p>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                    {t('inventoryManagement.transferModal.reason')} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <textarea
                    value={transferReason}
                    onChange={(e) => setTransferReason(e.target.value)}
                    placeholder={t('placeholders.transferReason')}
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      border: '1px solid var(--border)',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: '1rem',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn-action btn-secondary"
                onClick={closeTransferModal}
                disabled={submittingTransfer}
              >
                {tCommon('ui.cancel')}
              </button>
              <button
                className="btn-action btn-success"
                onClick={submitInventoryTransfer}
                disabled={submittingTransfer || !transferToLocation || !transferQuantity || !transferReason}
              >
                {submittingTransfer ? t('inventoryManagement.buttons.transferring') : `🔄 ${t('inventoryManagement.transferModal.title')}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

InventoryManagement.displayName = 'InventoryManagement';

export default InventoryManagement;
